/**
 * Detect respondent-facing strings that glue two question stems together.
 *
 * RND-2760: one persisted title held an agreement rating stem and a spend stem
 * with no delimiter (`...price?How much...`). Survey compile and the UI render
 * that string as-is; this guard catches that join at authoring and on draft
 * validate.
 *
 * RND-3003: a `?` immediately followed by a letter is glue only when both
 * clauses meeting there are multi-word (each trimmed side contains
 * whitespace). A one-word side is one prompt, in any script. Space-separated
 * question marks are one prompt. Counting question marks is not glue, and
 * neither is a list of lead-in words. A run of `?` is one boundary.
 *
 * Cache scope: one-shot, not cached.
 */

// Fresh each call: a shared /g regexp would keep lastIndex across titles.
function gluedLetter(): RegExp {
  return new RegExp("\\?\\p{L}", "gu");
}

// A clause is multi-word when whitespace survives trim. Leading and trailing
// spaces (including a French space before `?`) do not count, and the check
// is the whitespace, not which word is on either side.
function isMultiWordClause(clause: string): boolean {
  return /\s/u.test(clause.trim());
}

// The clause ends at this `?`. Extra `?` stuck to it (`??How`) belong to the
// same boundary, so the words before the run stay on the left.
function clauseBefore(text: string, questionMarkAt: number): string {
  let cursor = questionMarkAt - 1;
  while (cursor >= 0 && text.charAt(cursor) === "?") {
    cursor -= 1;
  }
  const previous = cursor >= 0 ? text.lastIndexOf("?", cursor) : -1;
  const start = previous >= 0 ? previous + 1 : 0;
  return text.slice(start, questionMarkAt);
}

function clauseAfter(text: string, questionMarkAt: number): string {
  const next = text.indexOf("?", questionMarkAt + 1);
  const end = next === -1 ? text.length : next;
  return text.slice(questionMarkAt + 1, end);
}

export function respondentTextGluesQuestionStems(
  text: string | null | undefined,
): boolean {
  if (text == null) {
    return false;
  }
  const stripped = text.trim();
  if (!stripped) {
    return false;
  }
  for (const match of stripped.matchAll(gluedLetter())) {
    const at = match.index ?? 0;
    if (
      isMultiWordClause(clauseBefore(stripped, at)) &&
      isMultiWordClause(clauseAfter(stripped, at))
    ) {
      return true;
    }
  }
  return false;
}
